
import { useMemo } from "react";
import type { Candidate } from "./types";
import FunnelFlow from "./FunnelFlow";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";

interface PipelineFunnelProps {
  candidates: Candidate[];
  isLoading?: boolean; // Added isLoading prop
}

export const PipelineFunnel = ({ candidates, isLoading = false }: PipelineFunnelProps) => {
  // Define the color function before using it in useMemo
  const getStageColor = (status: string) => {
    const colors = {
      new: "#9b87f5",
      contacted: "#7E69AB",
      interview: "#6E59A5",
      offer: "#8B5CF6",
      hired: "#33C3F0"
    };
    return colors[status as keyof typeof colors] || "#9b87f5";
  };

  const funnelData = useMemo(() => {
    // Define the flow order of statuses
    const funnelOrder = ["new", "contacted", "interview", "offer", "hired"];
    
    // Count candidates in each stage
    const stageCounts = funnelOrder.map(status => {
      const count = candidates.filter(c => c.status === status).length;
      return {
        status,
        count,
      };
    });
    
    // Calculate conversion rates between stages
    return stageCounts.map((stage, index) => {
      const prevCount = index > 0 ? stageCounts[index - 1].count : stage.count;
      const conversionRate = prevCount === 0 ? 0 : 
        Math.round((stage.count / prevCount) * 100);
      
      return {
        label: stage.status.charAt(0).toUpperCase() + stage.status.slice(1),
        count: stage.count,
        value: conversionRate,
        color: getStageColor(stage.status)
      };
    });
  }, [candidates]);

  if (isLoading) {
    return (
      <div className="w-full space-y-6">
        <h3 className="text-lg font-semibold">Recruitment Pipeline Analytics</h3>
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-8">
              <div className="w-3/5">
                <Skeleton className="h-[400px] w-full" />
              </div>
              <div className="w-2/5">
                <Skeleton className="h-8 w-full mb-4" />
                <Skeleton className="h-6 w-full mb-2" />
                <Skeleton className="h-6 w-full mb-2" />
                <Skeleton className="h-6 w-full mb-2" />
                <Skeleton className="h-6 w-full mb-2" />
                <Skeleton className="h-6 w-full" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      <h3 className="text-lg font-semibold">Recruitment Pipeline Analytics</h3>
      <FunnelFlow 
        stages={funnelData}
        height={400}
      />
    </div>
  );
};
